import type { ColumnDef } from "@tanstack/react-table";
import { Badge } from "@/components/ui/badge";
import { DataTableColumnHeader, ActionsCell } from "@/components/data-table";
import { msg } from "@lingui/core/macro";
import type { I18n } from "@lingui/core";
import type { Location } from "@/types/location";
import { isHeadquartersLocation } from "@/lib/locations";

export function getLocationColumns(
  i18n: I18n,
  navigate: (locationId: number) => void,
  getOrganizationName?: (location: Location) => string,
): ColumnDef<Location, unknown>[] {
  return [
    {
      accessorKey: "name",
      meta: { label: i18n._(msg`Name`) },
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={i18n._(msg`Name`)} />
      ),
      cell: ({ row, getValue }) => (
        <span className="flex items-center gap-2">
          <span className="font-medium">{getValue() as string}</span>
          {isHeadquartersLocation(row.original) && (
            <Badge variant="secondary">{i18n._(msg`HQ`)}</Badge>
          )}
        </span>
      ),
    },
    {
      accessorKey: "code",
      meta: { label: i18n._(msg`Code`) },
      header: i18n._(msg`Code`),
      cell: ({ getValue }) => (
        <span className="font-mono text-xs text-(--color-text-secondary)">
          {getValue() as string}
        </span>
      ),
      enableSorting: false,
    },
    {
      accessorKey: "company",
      meta: { label: i18n._(msg`Concept`) },
      header: i18n._(msg`Concept`),
      cell: ({ row }) => (
        <span className="text-sm text-(--color-text-secondary)">
          {row.original.company?.name ?? "—"}
        </span>
      ),
      enableSorting: false,
    },
    ...(getOrganizationName
      ? [
          {
            id: "organization",
            meta: { label: i18n._(msg`Organization`) },
            header: i18n._(msg`Organization`),
            cell: ({ row }: { row: { original: Location } }) => (
              <span className="text-sm text-(--color-text-secondary)">
                {getOrganizationName(row.original)}
              </span>
            ),
            enableSorting: false,
          } as ColumnDef<Location, unknown>,
        ]
      : []),
    {
      accessorKey: "city",
      meta: { label: i18n._(msg`City`) },
      header: i18n._(msg`City`),
      cell: ({ getValue }) => (
        <span className="text-sm text-(--color-text-secondary)">
          {(getValue() as string | undefined) ?? "—"}
        </span>
      ),
      enableSorting: false,
    },
    {
      accessorKey: "timezone",
      meta: { label: i18n._(msg`Timezone`), defaultVisible: false },
      header: i18n._(msg`Timezone`),
      cell: ({ getValue }) => (
        <span className="text-sm text-(--color-text-secondary)">
          {getValue() as string}
        </span>
      ),
      enableSorting: false,
    },
    {
      accessorKey: "region",
      meta: { label: i18n._(msg`Region`), defaultVisible: false },
      header: i18n._(msg`Region`),
      cell: ({ row }) => (
        <span className="text-sm text-(--color-text-secondary)">
          {row.original.region?.name ?? "—"}
        </span>
      ),
      enableSorting: false,
    },
    {
      accessorKey: "isActive",
      meta: { label: i18n._(msg`Status`) },
      header: i18n._(msg`Status`),
      cell: ({ getValue }) => (
        <Badge variant={getValue() ? "success" : "secondary"}>
          {getValue() ? i18n._(msg`Active`) : i18n._(msg`Inactive`)}
        </Badge>
      ),
      enableSorting: false,
    },
    {
      id: "actions",
      enableSorting: false,
      enableHiding: false,
      cell: ({ row }) => (
        <ActionsCell
          items={[
            { label: i18n._(msg`View`), onClick: () => navigate(row.original.id) },
            { label: i18n._(msg`Edit`), onClick: () => {} },
            { label: i18n._(msg`Deactivate`), onClick: () => {}, destructive: true },
          ]}
        />
      ),
    },
  ];
}
